export const LAYOUT_PRESETS = {
  full: {
    key: 'full',
    label: 'Full width',
    columns: [1],
  },
  half: {
    key: 'half',
    label: 'Half / Half',
    columns: [0.5, 0.5],
  },
  'one-third-two-thirds': {
    key: 'one-third-two-thirds',
    label: '1/3 + 2/3',
    columns: [1 / 3, 2 / 3],
  },
  'two-thirds-one-third': {
    key: 'two-thirds-one-third',
    label: '2/3 + 1/3',
    columns: [2 / 3, 1 / 3],
  },
};

export const CORE_FIELDS_LIMIT = 10;

export const PALETTE_WIDGET_TYPES = [
  { type: 'section', label: 'Section', icon: '▦' },
  { type: 'core-fields', label: 'Core fields', icon: '◈' },
  { type: 'text', label: 'Text', icon: 'T' },
  { type: 'image', label: 'Image', icon: '🖼' },
];

export function createEmptyLayout() {
  return { rows: [] };
}

export function layoutDisplayName(objectLabel, objectApiName) {
  const base = objectLabel || objectApiName || 'Object';
  return `${base} page layout`;
}

export function createRow(layoutKey) {
  const preset = LAYOUT_PRESETS[layoutKey] || LAYOUT_PRESETS.full;
  return {
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    layoutKey: preset.key,
    columns: preset.columns.map(() => ({ widgets: [] })),
  };
}

export function createWidget(payload) {
  const base = {
    id: `w-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: payload.type,
  };
  if (payload.type === 'section') {
    return { ...base, label: payload.label || 'Section' };
  }
  if (payload.type === 'core-fields') {
    return { ...base, label: payload.label || 'Core fields' };
  }
  if (payload.type === 'field') {
    return {
      ...base,
      fieldName: payload.fieldName,
      label: payload.label || payload.fieldName,
    };
  }
  if (payload.type === 'text') {
    return { ...base, content: payload.content || 'Text block' };
  }
  if (payload.type === 'image') {
    return { ...base, imageUrl: payload.imageUrl || '' };
  }
  return base;
}

export function normalizeLayout(raw) {
  if (!raw || typeof raw !== 'object') return createEmptyLayout();
  const rows = Array.isArray(raw.rows) ? raw.rows : [];
  return {
    rows: rows.map((row, rowIdx) => {
      const layoutKey = row.layoutKey || 'full';
      const preset = LAYOUT_PRESETS[layoutKey] || LAYOUT_PRESETS.full;
      const columns = Array.isArray(row.columns) ? row.columns : [];
      const normalizedCols = preset.columns.map((_, idx) => ({
        widgets: Array.isArray(columns[idx]?.widgets)
          ? columns[idx].widgets.map((w) => ({ ...w }))
          : [],
      }));
      return {
        id: row.id || `row-${rowIdx}-${Date.now()}`,
        layoutKey,
        columns: normalizedCols,
      };
    }),
  };
}

export function layoutEquals(a, b) {
  return JSON.stringify(normalizeLayout(a)) === JSON.stringify(normalizeLayout(b));
}

export function addWidgetToLayout(layout, rowId, columnIndex, widgetPayload) {
  const next = normalizeLayout(layout);
  const row = next.rows.find((r) => r.id === rowId);
  if (!row || columnIndex < 0 || columnIndex >= row.columns.length) return next;
  row.columns[columnIndex].widgets = [
    ...row.columns[columnIndex].widgets,
    createWidget(widgetPayload),
  ];
  return next;
}

export function removeWidgetFromLayout(layout, rowId, columnIndex, widgetId) {
  const next = normalizeLayout(layout);
  const row = next.rows.find((r) => r.id === rowId);
  if (!row || columnIndex < 0 || columnIndex >= row.columns.length) return next;
  row.columns[columnIndex].widgets = row.columns[columnIndex].widgets.filter(
    (w) => w.id !== widgetId
  );
  return next;
}

export function updateWidgetInLayout(layout, rowId, columnIndex, widgetId, patch) {
  const next = normalizeLayout(layout);
  const row = next.rows.find((r) => r.id === rowId);
  if (!row) return next;
  const col = row.columns[columnIndex];
  if (!col) return next;
  col.widgets = col.widgets.map((w) =>
    w.id === widgetId ? { ...w, ...patch } : w
  );
  return next;
}

export function moveWidgetInLayout(
  layout,
  fromRowId,
  fromColumnIndex,
  widgetId,
  toRowId,
  toColumnIndex
) {
  const next = normalizeLayout(layout);
  if (fromRowId === toRowId && fromColumnIndex === toColumnIndex) return next;

  const fromRow = next.rows.find((r) => r.id === fromRowId);
  const toRow = next.rows.find((r) => r.id === toRowId);
  if (!fromRow || !toRow) return next;

  const fromCol = fromRow.columns[fromColumnIndex];
  const toCol = toRow.columns[toColumnIndex];
  if (!fromCol || !toCol) return next;

  const idx = fromCol.widgets.findIndex((w) => w.id === widgetId);
  if (idx < 0) return next;

  const widget = fromCol.widgets[idx];
  fromCol.widgets = fromCol.widgets.filter((w) => w.id !== widgetId);
  toCol.widgets = [...toCol.widgets, widget];
  return next;
}

export function isMovePayload(payload) {
  return Boolean(payload?._move?.widgetId);
}

export function widgetDragLabel(widget) {
  if (!widget) return 'Widget';
  if (widget.type === 'field') return widget.label || widget.fieldName || 'Field';
  if (widget.type === 'section') return widget.label || 'Section';
  if (widget.type === 'core-fields') return widget.label || 'Core fields';
  if (widget.type === 'text') return (widget.content || 'Text').slice(0, 48);
  if (widget.type === 'image') return widget.imageUrl ? 'Image' : 'Image block';
  return widget.type || 'Widget';
}
